import { useEffect, useState } from "react";

import Button from "@stadium/ui/src/components/button/button";
import Input from "@stadium/ui/src/components/input/input";
import TippyTooltip from "@stadium/ui/src/components/tooltip/tippy-tooltip";

import useWithAnalytics from "@stadium/services/analytics/hooks/use-with-analytics";
import { ANALYTICS_EVENT_ACTION_SELECTED } from "@stadium/services/analytics/constants/default";

import { DEFAULT_QUANTITY } from "@constants/products/detail";
import { ANALYTICS_TARGET_QUANTITY } from "@constants/analytics/shop";

import styles from "./main.module.scss";

const QuantitySelector = ({
  minOrderableQuantity = DEFAULT_QUANTITY,
  quantity,
  setQuantity,
  className = `${styles.defaultStyle} t-br-1`,
  restrictZeroInput = false,
  onZeroHandler = null,
  decreaseBelowMinAllowed,
  disabled,
  eventLocationAnalytics = {},
}) => {
  const withAnalytics = useWithAnalytics();

  const [quantityValue, setQuantityValue] = useState(quantity);
  const allowQtyDecrease = quantityValue > minOrderableQuantity || decreaseBelowMinAllowed;

  useEffect(() => {
    if (quantity !== quantityValue || quantity < minOrderableQuantity) {
      const val =
        quantity < minOrderableQuantity || Number.isNaN(Number(quantity))
          ? minOrderableQuantity
          : quantity;
      updateQuantity(val);
    }
  }, [quantity]);

  const updateQuantity = (qty) => {
    setQuantityValue(qty);
    if (qty !== quantity) {
      setQuantity(qty);

      if (eventLocationAnalytics.track) {
        withAnalytics(undefined, {
          ...eventLocationAnalytics,
          target: eventLocationAnalytics?.target ?? ANALYTICS_TARGET_QUANTITY,
          action: eventLocationAnalytics?.action ?? ANALYTICS_EVENT_ACTION_SELECTED,
          eventProperties: {
            ...eventLocationAnalytics.eventProperties,
            quantity: qty,
          },
        })();
      }
    }
  };

  const reduceQuantity = (e) => {
    e.stopPropagation();
    e.preventDefault();

    if (quantityValue > minOrderableQuantity && quantityValue > 1) {
      updateQuantity(quantityValue - 1);
    } else {
      onZeroHandler?.();
    }
  };

  const addQuantity = (e) => {
    e.stopPropagation();
    e.preventDefault();
    updateQuantity(quantityValue + 1);
  };

  const handleQuantitySelector = async ({ value }) => {
    if (!Number.isNaN(Number(value))) setQuantityValue(Number(value));
  };

  const handleQuantitySelectorClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
  };
  return (
    <aside className={`qtyControllerGroup ${className}`}>
      <Button
        customClass="icon-subtract qtyBox"
        onClick={!disabled && allowQtyDecrease ? reduceQuantity : null}
        isDisabled={!allowQtyDecrease || disabled}
      >
        <TippyTooltip
          content={<span>You cannot remove an item as minimum is {minOrderableQuantity}</span>}
          isLightTheme={true}
          placement="top"
          arrow={true}
          disabled={allowQtyDecrease || disabled}
          customClassName={styles.platformTippyText}
        >
          <i className="icon-minus"></i>
        </TippyTooltip>
      </Button>
      <Input
        type="text"
        value={quantityValue}
        onClick={handleQuantitySelectorClick}
        onChange={handleQuantitySelector}
        inputWrapperClass="numeric-input-container"
        customClass="qtyNumber"
        isDisabled={disabled}
        onBlur={() => {
          if (Number(quantityValue) === 0) {
            if (restrictZeroInput) {
              updateQuantity(minOrderableQuantity);
              return;
            }
            if (typeof onZeroHandler === "function") {
              return onZeroHandler();
            }
          }
          if (quantityValue < minOrderableQuantity) {
            return updateQuantity(minOrderableQuantity);
          }
          updateQuantity(quantityValue);
        }}
      />
      <Button
        customClass="icon-add qtyBox"
        onClick={disabled ? null : addQuantity}
        isDisabled={disabled}
      >
        <i className="icon-plus"></i>
      </Button>
    </aside>
  );
};

export default QuantitySelector;
